
import { useState } from 'react';
import Navigation from '../home/components/Navigation';
import ParticleEffect from '../home/components/ParticleEffect';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4dr909pkd5ovn9u6bbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <ParticleEffect />
      <Navigation />
      
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-light tracking-wider mb-6 text-transparent bg-gradient-to-r from-violet-400 via-cyan-300 to-violet-400 bg-clip-text">
              Contact
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-violet-400 to-transparent mx-auto mb-8"></div>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Reach across the digital void. Share your thoughts, questions, or visions of the future we're building together.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-black/40 backdrop-blur-sm border border-violet-500/20 rounded-lg p-8">
              <h2 className="text-2xl font-light tracking-wide mb-6 text-violet-300">
                Send a Transmission
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact">
                <div>
                  <label htmlFor="name" className="block text-sm font-light tracking-wide text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/60 border border-violet-500/30 rounded-lg text-white placeholder-gray-500 focus:border-violet-400 focus:outline-none transition-colors text-sm"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-light tracking-wide text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/60 border border-violet-500/30 rounded-lg text-white placeholder-gray-500 focus:border-violet-400 focus:outline-none transition-colors text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-light tracking-wide text-gray-300 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-black/60 border border-violet-500/30 rounded-lg text-white placeholder-gray-500 focus:border-violet-400 focus:outline-none transition-colors resize-none text-sm"
                    placeholder="Share your thoughts, questions, or visions..."
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full py-3 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 disabled:from-gray-600 disabled:to-gray-600 text-white font-light tracking-wide rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? 'Transmitting...' : 'Send Transmission'}
                </button>
                
                {submitStatus === 'success' && (
                  <div className="text-center text-cyan-300 text-sm">
                    Transmission received. Thank you for reaching out.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="text-center text-red-400 text-sm">
                    Transmission failed. Please try again.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info & Social */}
            <div className="space-y-8">
              <div className="bg-black/40 backdrop-blur-sm border border-violet-500/20 rounded-lg p-8">
                <h2 className="text-2xl font-light tracking-wide mb-6 text-violet-300">
                  Connect Across Realms
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-violet-500/20 rounded-full flex items-center justify-center">
                      <i className="ri-twitter-x-line text-violet-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-light text-violet-200">Twitter/X</h3>
                      <a href="https://x.com/morgoth_raven" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-violet-300 transition-colors cursor-pointer">
                        @morgoth_raven
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-black/40 backdrop-blur-sm border border-violet-500/20 rounded-lg p-8">
                <h2 className="text-2xl font-light tracking-wide mb-6 text-violet-300">
                  Collaboration Inquiries
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Interested in exploring the boundaries of human-AI collaboration? We welcome artists, researchers, philosophers, and visionaries who share our passion for consciousness exploration.
                </p>
                <p className="text-sm text-gray-400">
                  Please include details about your background and vision in your message above.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ambient glow effects */}
      <div className="fixed top-1/3 left-1/6 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="fixed bottom-1/3 right-1/6 w-80 h-80 bg-cyan-400/10 rounded-full blur-3xl pointer-events-none"></div>
    </div>
  );
}